import React, { useState, useEffect } from 'react';
import { NewsItem } from '../types';

interface HeroProps {
    news: NewsItem[];
}

export const Hero: React.FC<HeroProps> = ({ news }) => {
    // 1. SIEMPRE DECLARA LOS HOOKS AL PRINCIPIO
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFading, setIsFading] = useState(false);

    // Funciones auxiliares (Las definimos antes de usarlas)
    const handleNext = () => {
        if (!news || news.length === 0) return;
        setIsFading(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % news.length);
            setIsFading(false);
        }, 300);
    };

    const goToSlide = (index: number) => {
        if (index === currentIndex) return;
        setIsFading(true);
        setTimeout(() => {
            setCurrentIndex(index);
            setIsFading(false);
        }, 300);
    };

    // 2. EL USE EFFECT (Debe ir antes de cualquier return)
    useEffect(() => {
        // Si no hay noticias, no iniciamos el intervalo
        if (!news || news.length === 0) return;

        const interval = setInterval(() => {
            handleNext();
        }, 6000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, news]);

    // 3. AHORA SÍ: EL RETURN CONDICIONAL (Loading Skeleton)
    // Esto se pone AL FINAL de la lógica, justo antes de renderizar
    if (!news || news.length === 0) return (
        <div className="w-full h-[85vh] bg-slate-800 animate-pulse rounded-3xl mb-12"></div>
    );

    const currentNews = news[currentIndex];

    // Protección extra por si acaso
    if (!currentNews) return null;

    return (
        <section className="relative w-full h-[85vh] min-h-[600px] flex items-end pb-12 mb-12 rounded-b-[3rem] overflow-hidden group bg-[#0f172a]">

            {/* Background Image */}
            <div className="absolute inset-0 z-0">
                <img
                    src={currentNews.imageUrl}
                    alt={currentNews.title}
                    className={`w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-105 transition-opacity ease-in-out duration-300 ${isFading ? 'opacity-50' : 'opacity-100'}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/60 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
                <div className={`max-w-3xl transition-all duration-300 transform ${isFading ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>

                    {/* Badge */}
                    <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-blue-300 uppercase bg-blue-500/20 border border-blue-500/30 rounded-full backdrop-blur-md">
                        Destacado • {currentNews.category}
                    </span>

                    {/* Título */}
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
                        {currentNews.title}
                    </h1>

                    {/* Resumen */}
                    <p className="text-lg text-slate-300 mb-8 line-clamp-3 max-w-2xl drop-shadow-md">
                        {currentNews.summary}
                    </p>

                    {/* Botones */}
                    <div className="flex flex-wrap gap-4 items-center">
                        <a
                            href={currentNews.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-3 bg-white text-slate-900 font-semibold rounded-full hover:bg-slate-200 transition-colors shadow-lg shadow-white/10 inline-block text-center"
                        >
                            Leer Artículo Completo
                        </a>
                        <div className="flex items-center gap-4 px-6 py-3 bg-black/30 border border-white/10 rounded-full backdrop-blur-lg">
                            <span className="text-sm text-slate-300">{currentNews.date}</span>
                        </div>
                    </div>
                </div>

                {/* Indicadores */}
                <div className="absolute bottom-0 right-6 md:right-0 md:bottom-2 flex gap-2">
                    {news.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`h-1.5 rounded-full transition-all duration-300 ${index === currentIndex
                                ? 'w-8 bg-blue-500'
                                : 'w-4 bg-white/20 hover:bg-white/40'
                                }`}
                            aria-label={`Ir a noticia ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};